import type { ThunkAction, ThunkDispatch } from "@reduxjs/toolkit";
import type { Unsubscribe } from "firebase/firestore";

import type { ReduxState } from "@dashboard/types/reduxState";
import type { ThunkExtraArgument } from "@dashboard/types/thunk";

import type {
  CreateMenuUrlV2Input,
  UpdateMenuUrlV2Input,
} from "../backendApis/menuUrlV2Api";
import type { ErrorReducerAction } from "../reducers/errorReducer";
import type { MenuUrlV2ReducerAction } from "../reducers/menuUrlV2Reducer";
import { addError } from "./addErrorAction";

type MenuUrlV2Action = MenuUrlV2ReducerAction | ErrorReducerAction;

type ThunkActionReturn = ThunkAction<
  Promise<void>,
  ReduxState,
  ThunkExtraArgument,
  MenuUrlV2Action
>;

type MenuUrlV2SaveThunkAction = ThunkAction<
  Promise<boolean>,
  ReduxState,
  ThunkExtraArgument,
  MenuUrlV2Action
>;

export type MenuUrlV2ThunkDispatch = ThunkDispatch<
  ReduxState,
  ThunkExtraArgument,
  MenuUrlV2Action
>;

// Live listener: returns the unsubscribe synchronously (not a promise).
export const subscribeToMenuUrlsV2 =
  (
    restaurantUuid: string
  ): ThunkAction<
    Unsubscribe,
    ReduxState,
    ThunkExtraArgument,
    MenuUrlV2Action
  > =>
  (dispatch, _getState, { menuUrlV2Api }) =>
    menuUrlV2Api.subscribeToMenuUrlsV2(
      restaurantUuid,
      (payload) => dispatch({ type: "FETCHED_MENU_URLS_V2", payload }),
      (error) => {
        dispatch({ type: "FETCH_MENU_URLS_V2_FAILED" });
        addError(dispatch)(error);
      }
    );

/**
 * Creates a new MenuUrlV2.
 */
export const createMenuUrlV2: (
  input: Omit<CreateMenuUrlV2Input, "restaurantUuid">
) => MenuUrlV2SaveThunkAction = (input) => {
  return async (
    dispatch: MenuUrlV2ThunkDispatch,
    getState: () => ReduxState,
    { menuUrlV2Api }: ThunkExtraArgument
  ) => {
    const currentRest = getState().restaurant.selectedRestaurant;
    if (!currentRest) {
      addError(dispatch)("No restaurant selected");
      return false;
    }

    try {
      await menuUrlV2Api.createMenuUrlV2({
        ...input,
        restaurantUuid: currentRest.uuid,
      });
      return true;
    } catch (error) {
      addError(dispatch)(error as Error | string);
      return false;
    }
  };
};

/**
 * Updates an existing MenuUrlV2.
 */
export const updateMenuUrlV2: (
  docId: string,
  updates: UpdateMenuUrlV2Input
) => MenuUrlV2SaveThunkAction = (docId, updates) => {
  return async (
    dispatch: MenuUrlV2ThunkDispatch,
    getState: () => ReduxState,
    { menuUrlV2Api }: ThunkExtraArgument
  ) => {
    const currentRest = getState().restaurant.selectedRestaurant;
    if (!currentRest) {
      addError(dispatch)("No restaurant selected");
      return false;
    }

    try {
      await menuUrlV2Api.updateMenuUrlV2(currentRest.uuid, docId, updates);
      return true;
    } catch (error) {
      addError(dispatch)(error as Error | string);
      return false;
    }
  };
};

/**
 * Deletes a MenuUrlV2.
 */
export const deleteMenuUrlV2: (docId: string) => ThunkActionReturn = (
  docId
) => {
  return (
    dispatch: MenuUrlV2ThunkDispatch,
    getState: () => ReduxState,
    { menuUrlV2Api }: ThunkExtraArgument
  ) => {
    const currentRest = getState().restaurant.selectedRestaurant;
    if (!currentRest) {
      return Promise.reject("No restaurant selected");
    }

    return menuUrlV2Api
      .deleteMenuUrlV2(currentRest.uuid, docId)
      .catch(addError(dispatch));
  };
};

/**
 * Check if a URL slug already exists in the menu-urls-v2 collection group.
 * Returns true if the slug exists (excluding the current docId if provided).
 * Used for uniqueness validation before create/update.
 */
export const checkUrlSlugExistsV2 = (
  urlSlug: string,
  excludeDocId?: string
): ThunkAction<Promise<boolean>, ReduxState, ThunkExtraArgument, never> => {
  return (_dispatch, _getState, { menuUrlV2Api }: ThunkExtraArgument) => {
    return menuUrlV2Api.checkUrlSlugExists(urlSlug, excludeDocId);
  };
};
